import { EAppSettings } from "library/types";
import { useGetUserAppTargetSetting } from "./useGetUserAppTargetSetting";

type Props = {
  benefitKey: string;
  defaultOrderedList: any[];
  rawList: any[];
  itemIdProperty: string;
};

const getNextId = (list: { id: string }[], index): string => {
  const maxId = list.reduce((max, item) => {
    const idNum = Number(item.id);
    return isNaN(idNum) ? max : Math.max(max, idNum);
  }, 0);

  return (maxId + 1 + index).toString();
};

export function useBenefitCardsOrderListConfig({
  benefitKey,
  defaultOrderedList,
  itemIdProperty,
  rawList,
}: Props) {
  const benefitCardsOrderListAppSettings = useGetUserAppTargetSetting({
    targetSettingName: EAppSettings.benefitCardsOrderListConfig,
  });

  const configByCardType = benefitCardsOrderListAppSettings?.userAppSettingValue
    ? JSON.parse(benefitCardsOrderListAppSettings.userAppSettingValue)[
        benefitKey
      ]
    : null;

  const allCardsOrderConfig =
    benefitCardsOrderListAppSettings?.userAppSettingValue
      ? JSON.parse(benefitCardsOrderListAppSettings.userAppSettingValue)
      : null;

  const orderedList = configByCardType
    ? [
        // First, map items from configByCardType in order
        ...configByCardType
          .map((configItem, index) => {
            const match = rawList.find(
              (benefit) =>
                benefit[itemIdProperty]?.toString() ===
                configItem.tag?.toString()
            );
            if (match) {
              return {
                ...match,
                tag: configItem.tag,
                id: configItem.id || (index + 1).toString(),
              };
            }
            return null;
          })
          .filter(Boolean),

        // Then, add unmatched items from rawList (those not in configByCardType)
        ...rawList
          .filter(
            (benefit) =>
              !configByCardType.some(
                (item) =>
                  item.tag?.toString() === benefit[itemIdProperty]?.toString()
              )
          )
          .map((benefit, index) => ({
            ...benefit,
            tag: benefit[itemIdProperty]?.toString(),
            id: getNextId(configByCardType, index), // or some other fallback
          })),
      ]
    : defaultOrderedList;

  return {
    benefitCardsOrderListAppSettings,
    allCardsOrderConfig,
    orderedList,
  };
}
